import { inject, Injectable } from '@angular/core';

import { DatePipe } from '@angular/common';
import { IResponsible, IUser, ResponsibleType } from '@shared/interfaces';
import { Workbook } from 'exceljs';
import { FileSaverService } from 'ngx-filesaver';
import { compare } from '@shared/helpers/helpers';
import dateFormat from 'dateformat';
import { TranslateService } from '@ngx-translate/core';
import { CURRENT_PROJECT } from '../../../current-project';

@Injectable({
    providedIn: 'root'
})
export class ExportService {
    private readonly datePipe = inject(DatePipe);
    private readonly fileSaver = inject(FileSaverService);
    private readonly translateService = inject(TranslateService);

    public async generateExcelForUsers(user: IUser[]): Promise<void> {
        const title = 'Authorized Users';

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Authorized Users');

        const titleRow = worksheet.addRow([title]);
        titleRow.font = { size: 16, bold: true };
        worksheet.addRow([]);
        worksheet.addRow(['Date : ' + this.datePipe.transform(new Date(), 'medium')]);

        worksheet.mergeCells('A1:V2');
        worksheet.addRow([]);

        const headerRow = worksheet.addRow([
            'Full Name',
            'GID',
            'EMail',
            'Department',
            'Department Description',
            'Role'
        ]);
        headerRow.alignment = { horizontal: 'center' };
        headerRow.eachCell((cell, number) => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFF00' },
                bgColor: { argb: 'FF0000FF' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        user.forEach(d => {
            const row = worksheet.addRow([
                `${d.userData.fullName}`,
                d.id,
                d.userData.eMail,
                d.userData.department,
                d.userData.departmentText,
                d.userRole
            ]);
        });

        worksheet.getColumn(1).width = 30;
        worksheet.getColumn(2).width = 20;
        worksheet.getColumn(3).width = 40;
        worksheet.getColumn(4).width = 40;
        worksheet.getColumn(5).width = 40;
        worksheet.getColumn(6).width = 15;
        worksheet.getColumn(7).width = 15;

        let rowIndex = 1;
        for (rowIndex; rowIndex <= worksheet.rowCount; rowIndex++) {
            worksheet.getRow(rowIndex).alignment = { vertical: 'top', wrapText: true };
        }

        const date = new Date();
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        this.fileSaver.save(blob, `AuthorizedUsers_${dateFormat(date, 'yyyy-mm-dd-HH-MM-ss')}.xlsx`);
    }

    public async generateExcelForRS(items: IResponsible[]): Promise<void> {
        const projectAbbreviation = this.translateService.instant(`project.${CURRENT_PROJECT}.abbreviation`);
        const date = new Date();
        const workbook = new Workbook();
        let worksheet = workbook.addWorksheet('SAG Company Units');

        let titleRow = worksheet.addRow([`${projectAbbreviation} Responsibles - SAG Company Units`]);
        titleRow.font = { size: 16, bold: true };
        worksheet.addRow([]);
        worksheet.addRow(['Date : ' + this.datePipe.transform(date, 'medium')]);

        worksheet.mergeCells('A1:V2');
        worksheet.addRow([]);

        let headerRow = worksheet.addRow([
            'SAG Company Unit',
            'Organization Code',
            'Member of the Executive Management (main)',
            'Member of the Executive Management (main) (email)',
            'TRSM-O Business (main)',
            'TRSM-O Business (main) (email)',
            'Member of the Executive Management (other)',
            'TRSM-O Business (other)',
            'Reporting hierarchy in Tool',
            'Parent OrgCode',
            'Created By'
        ]);
        headerRow.alignment = { horizontal: 'center' };
        headerRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFF00' },
                bgColor: { argb: 'FF0000FF' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        items
            .filter(e => e.responsibleType === ResponsibleType.sag)
            .sort((a, b) => compare(a.orgLevel ?? 1, b.orgLevel ?? 1) || compare(a.orgDescription, b.orgDescription))
            .forEach(d => {
                worksheet.addRow([
                    d.orgDescription,
                    d.orgCode,
                    d.responsible
                        .filter(e => e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    d.responsible
                        .filter(e => e.fullAccess)
                        .map(e => e.eMail)
                        .join(', '),
                    d.specialists
                        .filter(e => e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    d.specialists
                        .filter(e => e.fullAccess)
                        .map(e => e.eMail)
                        .join(', '),
                    d.responsible
                        .filter(e => !e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    d.specialists
                        .filter(e => !e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    d.orgLevel,
                    d.parentOrgCode?.startsWith('responsibility-') ? '' : d.parentOrgCode,
                    d.createdBy?.fullName || 'Gunnar Tolksdorf'
                ]);
            });

        worksheet.getColumn(1).width = 30;
        worksheet.getColumn(2).width = 20;
        worksheet.getColumn(3).width = 50;
        worksheet.getColumn(4).width = 50;
        worksheet.getColumn(5).width = 50;
        worksheet.getColumn(6).width = 50;
        worksheet.getColumn(7).width = 50;
        worksheet.getColumn(8).width = 50;
        worksheet.getColumn(9).width = 20;
        worksheet.getColumn(10).width = 50;
        worksheet.getColumn(11).width = 50;

        let rowIndex = 1;
        for (rowIndex; rowIndex <= worksheet.rowCount; rowIndex++) {
            worksheet.getRow(rowIndex).alignment = { vertical: 'top', wrapText: true };
        }

        worksheet = workbook.addWorksheet('Lead Countries');
        titleRow = worksheet.addRow([`${projectAbbreviation} Responsibles - Lead Countries`]);
        titleRow.font = { size: 16, bold: true };
        worksheet.addRow([]);
        worksheet.addRow(['Date : ' + this.datePipe.transform(date, 'medium')]);

        headerRow = worksheet.addRow([
            'Lead Country',
            'Code',
            'Member of the Executive Management (main)',
            'Member of the Executive Management (main) (email)',
            'TRSM-RC Lead Country (main)',
            'TRSM-RC Lead Country (main) (email)',
            'Member of the Executive Management (other)',
            'TRSM-RC Lead Country (other)',
            'Assigned Countries',
            'Reporting hierarchy in Tool',
            'Parent OrgCode',
            'CreatedBy'
        ]);
        headerRow.alignment = { horizontal: 'center' };
        headerRow.eachCell(cell => {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFFFF00' },
                bgColor: { argb: 'FF0000FF' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        items
            .filter(e => e.responsibleType === ResponsibleType.rc)
            .sort((a, b) => compare(a.orgLevel ?? 1, b.orgLevel ?? 1) || compare(a.orgDescription, b.orgDescription))
            .forEach(d => {
                worksheet.addRow([
                    d.orgDescription,
                    d.orgCode,
                    d.responsible
                        .filter(e => e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    d.responsible
                        .filter(e => e.fullAccess)
                        .map(e => e.eMail)
                        .join(', '),
                    d.specialists
                        .filter(e => e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    d.specialists
                        .filter(e => e.fullAccess)
                        .map(e => e.eMail)
                        .join(', '),
                    d.responsible
                        .filter(e => !e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    d.specialists
                        .filter(e => !e.fullAccess)
                        .map(e => e.fullName)
                        .join(', '),
                    (d.assignedCountries ?? []).join(', '),
                    d.orgLevel,
                    d.parentOrgCode?.startsWith('responsibility-') ? '' : d.parentOrgCode,
                    d.createdBy?.fullName || 'Gunnar Tolksdorf'
                ]);
            });

        worksheet.getColumn(1).width = 30;
        worksheet.getColumn(2).width = 20;
        worksheet.getColumn(3).width = 50;
        worksheet.getColumn(4).width = 50;
        worksheet.getColumn(5).width = 50;
        worksheet.getColumn(6).width = 50;
        worksheet.getColumn(7).width = 50;
        worksheet.getColumn(8).width = 50;
        worksheet.getColumn(9).width = 100;
        worksheet.getColumn(10).width = 20;
        worksheet.getColumn(11).width = 50;
        worksheet.getColumn(12).width = 50;

        rowIndex = 1;
        for (rowIndex; rowIndex <= worksheet.rowCount; rowIndex++) {
            worksheet.getRow(rowIndex).alignment = { vertical: 'top', wrapText: true };
        }

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        this.fileSaver.save(
            blob,
            `${projectAbbreviation}_Responsibles_${dateFormat(date, 'yyyy-mm-dd-HH-MM-ss')}.xlsx`
        );
    }
}
